(() => {
  const main = document.querySelector('main[data-template]');
  const template = main?.dataset.template || '';

  function publish(action, source, details = {}) {
    if (!action) return;

    const section = source?.closest?.('[data-sr-track-section]');
    const payload = {
      event: 'solis_interaction',
      action,
      section: section?.dataset.srTrackSection || '',
      path: window.location.pathname,
      template,
      timestamp: new Date().toISOString(),
      ...details,
    };

    if (section?.dataset.srFitContext) {
      payload.fit_context = section.dataset.srFitContext;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    document.dispatchEvent(new CustomEvent('solis:interaction', { detail: payload }));

    try {
      window.Shopify?.analytics?.publish?.('solis_interaction', payload);
    } catch (_error) {
      // Analytics adapters should never interrupt shopping.
    }
  }

  function getFitCheckVariant() {
    const allowedVariants = ['control', 'progressive'];
    const queryVariant = new URLSearchParams(window.location.search).get('fit-check-variant');

    if (allowedVariants.includes(queryVariant)) {
      return queryVariant;
    }

    const storageKey = 'sr_fit_check_variant_v1';
    const expiry = 1000 * 60 * 60 * 24 * 30;

    try {
      const stored = JSON.parse(window.localStorage.getItem(storageKey));

      if (allowedVariants.includes(stored?.variant) && stored.expires_at > Date.now()) {
        return stored.variant;
      }

      const variant = window.crypto?.getRandomValues
        ? window.crypto.getRandomValues(new Uint32Array(1))[0] % 2 === 0
          ? 'control'
          : 'progressive'
        : Math.random() < 0.5
          ? 'control'
          : 'progressive';

      window.localStorage.setItem(
        storageKey,
        JSON.stringify({ variant, expires_at: Date.now() + expiry })
      );

      return variant;
    } catch (_error) {
      return Math.random() < 0.5 ? 'control' : 'progressive';
    }
  }

  function createFitCheckSubmissionId() {
    if (window.crypto?.randomUUID) {
      return `fc_${window.crypto.randomUUID()}`;
    }

    if (window.crypto?.getRandomValues) {
      const values = window.crypto.getRandomValues(new Uint32Array(4));
      return `fc_${Array.from(values, (value) => value.toString(36)).join('')}`;
    }

    return `fc_${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;
  }

  function assignFitCheckSubmissionId(form) {
    const field = form.querySelector('[data-sr-fit-submission-id-field]');

    if (field && !field.value) {
      field.value = createFitCheckSubmissionId();
    }
  }

  function announceFitCheckSuccess(form, variant) {
    const successKey = `sr_fit_check_success_v1:${window.location.pathname}`;

    try {
      if (window.sessionStorage.getItem(successKey)) return;
      window.sessionStorage.setItem(successKey, 'true');
    } catch (_error) {}

    publish('fit_check_form_success', form, { variant });
  }

  function createFitCheckButton(text, className) {
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = text;
    button.className = className;
    return button;
  }

  function watchFitCheckVisibility(root) {
    if (root.dataset.srFitChatWatch === 'true' || !('IntersectionObserver' in window)) return;

    root.dataset.srFitChatWatch = 'true';
    const observer = new IntersectionObserver(
      ([entry]) => document.body.classList.toggle('sr-fit-check-in-view', entry.isIntersecting),
      { threshold: 0.2 }
    );

    observer.observe(root);
  }

  function enhanceFitCheck(root, form, variant) {
    const preferences = form.querySelector('[data-sr-fit-group="preferences"]');
    const goalField = preferences?.querySelector('select[name="contact[Recovery goal]"]')?.closest('label');
    const budgetField = preferences?.querySelector('select[name="contact[Budget range]"]')?.closest('label');
    const submitButton = form.querySelector('[data-sr-fit-submit]');
    const dataNote = form.querySelector('.sr-fit-check__data-note');

    if (!preferences || !goalField || !budgetField || !submitButton) return;

    function createFitCheckFieldGroup(field) {
      const group = document.createElement('div');
      group.className = 'sr-fit-check__field-group';
      group.append(field);
      return group;
    }

    const groups = {
      goal: createFitCheckFieldGroup(goalField),
      budget: createFitCheckFieldGroup(budgetField),
      notes: form.querySelector('[data-sr-fit-group="notes"]'),
      contact: form.querySelector('[data-sr-fit-group="contact"]'),
    };

    if (!groups.notes || !groups.contact) return;

    preferences.remove();

    const steps = [
      {
        group: groups.goal,
        title: 'What are you considering?',
        hint: 'Choose the closest starting point. You can update this later.',
      },
      {
        group: groups.budget,
        title: 'Do you have a budget in mind?',
        hint: 'This is optional. Choose “Not sure yet” or continue when you are ready.',
      },
      {
        group: groups.notes,
        title: 'What should we account for?',
        hint: 'A few details about placement, access, or setup are enough.',
      },
      {
        group: groups.contact,
        title: 'Where should we reply?',
        hint: 'Add your contact details and we will reply within one business day.',
      },
    ];
    const wizard = document.createElement('div');
    const progress = document.createElement('p');
    const panels = [];
    let activeStep = form.querySelector('.sr-form-errors') ? steps.length - 1 : 0;
    let started = false;

    wizard.className = 'sr-fit-check__wizard';
    wizard.dataset.srFitWizard = 'true';
    progress.className = 'sr-fit-check__wizard-progress';
    progress.setAttribute('aria-live', 'polite');
    wizard.append(progress);

    function markStarted() {
      if (started) return;
      started = true;
      publish('fit_check_form_start', form, { variant });
    }

    function showStep(nextStep, shouldFocus) {
      activeStep = nextStep;

      panels.forEach((panel, index) => {
        const isActive = index === activeStep;
        panel.hidden = !isActive;
        panel.setAttribute('aria-hidden', String(!isActive));
      });

      progress.textContent = `Step ${activeStep + 1} of ${panels.length}`;

      if (shouldFocus) {
        const heading = panels[activeStep].querySelector('.sr-fit-check__wizard-heading');
        heading?.focus({ preventScroll: true });
        panels[activeStep].scrollIntoView({
          block: 'center',
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      }
    }

    function validatePanel(panel) {
      const invalidField = Array.from(panel.querySelectorAll('input, select, textarea')).find(
        (field) => !field.checkValidity()
      );

      if (!invalidField) return true;

      invalidField.focus();
      invalidField.reportValidity();
      return false;
    }

    steps.forEach((step, index) => {
      const panel = document.createElement('section');
      const heading = document.createElement('h3');
      const hint = document.createElement('p');
      const actions = document.createElement('div');
      const headingId = `sr-fit-check-step-${index + 1}`;

      panel.className = 'sr-fit-check__wizard-panel';
      panel.dataset.srFitStep = String(index + 1);
      panel.setAttribute('aria-labelledby', headingId);
      heading.className = 'sr-fit-check__wizard-heading';
      heading.id = headingId;
      heading.tabIndex = -1;
      heading.textContent = step.title;
      hint.className = 'sr-fit-check__wizard-hint';
      hint.textContent = step.hint;
      actions.className = 'sr-fit-check__wizard-actions';

      panel.append(heading, hint, step.group, actions);

      if (index > 0) {
        const backButton = createFitCheckButton('Back', 'sr-fit-check__wizard-back');
        backButton.addEventListener('click', () => {
          markStarted();
          publish('fit_check_step_back', form, { variant, step: index + 1 });
          showStep(index - 1, true);
        });
        actions.append(backButton);
      }

      if (index === steps.length - 1) {
        actions.append(submitButton);
      } else {
        const nextButton = createFitCheckButton('Continue', 'sr-button sr-button--primary');
        nextButton.addEventListener('click', () => {
          markStarted();

          if (!validatePanel(panel)) return;

          publish('fit_check_step_complete', form, { variant, step: index + 1 });
          showStep(index + 1, true);
        });
        actions.append(nextButton);
      }

      panels.push(panel);
      wizard.append(panel);
    });

    form.append(wizard);
    if (dataNote) form.append(dataNote);
    form.addEventListener('input', markStarted, { once: true });
    form.addEventListener('change', markStarted, { once: true });
    root.dataset.srFitVariant = variant;
    showStep(activeStep, false);
  }

  function initFitChecks(scope = document) {
    scope.querySelectorAll?.('[data-sr-fit-root]').forEach((root) => {
      if (root.dataset.srFitReady === 'true') return;

      const form = root.querySelector('[data-sr-fit-form]');
      if (!form) return;

      root.dataset.srFitReady = 'true';
      watchFitCheckVisibility(root);
      const variant = root.dataset.srFitSimple === 'true' ? 'control' : getFitCheckVariant();
      const variantField = form.querySelector('[data-sr-fit-variant-field]');

      root.dataset.srFitVariant = variant;
      form.dataset.srFitVariant = variant;
      if (variantField) variantField.value = variant;

      if (form.dataset.srFitSuccess === 'true') {
        announceFitCheckSuccess(form, variant);
        return;
      }

      assignFitCheckSubmissionId(form);

      publish('fit_check_form_view', form, { variant });

      if (variant === 'progressive') {
        enhanceFitCheck(root, form, variant);
      }
    });
  }

  document.addEventListener('click', (event) => {
    const tracked = event.target.closest('[data-sr-track]');
    if (tracked) {
      if (tracked.closest('product-card') && event.target.closest('button, input, select, summary')) return;
      publish(tracked.dataset.srTrack, tracked);
      return;
    }

    const addButton = event.target.closest('button[name="add"], [type="submit"][name="add"]');
    if (addButton) {
      publish('add_to_cart_click', addButton);
      return;
    }

    const checkoutButton = event.target.closest('#checkout, [name="checkout"]');
    if (checkoutButton) {
      publish('checkout_click', checkoutButton);
    }
  });

  document.addEventListener('submit', (event) => {
    if (event.target?.id === 'SolisFitCheckForm') {
      publish('fit_check_form_submit', event.target, {
        variant: event.target.dataset.srFitVariant || 'control',
      });
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initFitChecks(), { once: true });
  } else {
    initFitChecks();
  }

  document.addEventListener('shopify:section:load', (event) => initFitChecks(event.target));
})();
